import React, { Component } from "react";
import { Modal, Button, Tabs, notification } from "antd";
import {
  RocketOutlined, ClearOutlined, BellOutlined, CheckCircleOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

// ── CONFIG: bump APP_VERSION and update WHATS_NEW on every deploy ──────────
const APP_VERSION = 2;  // increment this with each meaningful deploy

const WHATS_NEW = [
  {
    version: 2,
    date:    "May 31, 2026",
    title:   "Home Buddha Portal Ads, Network Improvements & SMS Setup",
    features: [
      "🏡 Home Buddha portal now supports advertising — sidebar and per-subsection ad slots across all 36 locations",
      "🌐 MyNetwork now catches all referred users across all referral field variants",
      "📱 SMS verification infrastructure added — toll-free number registered, opt-in on signup",
      "📊 Missed Leads now supports Agent, Lender, and Vendor assignment types",
      "🔗 Share with Client now works from the AI site — clients receive branded report emails",
      "💬 Buy and Sell tabs now include helpful descriptions for buyers and sellers",
      "🔔 New version notifications added so you always know when updates are available",
    ],
  },
  {
    version: 1,
    date:    "May 2026",
    title:   "Home Buddha Portal, Client Sharing & Agent Branding",
    features: [
      "🏠 Home Buddha portal launched — clients can see AI reports and home health data",
      "🤝 Agents can share reports directly to clients from the AI site",
      "👤 Agent branding shows on client portals",
      "📋 Saved reports now visible in client Home Buddha portal",
      "🌱 MyNetwork tracks buyers, sellers, homeowners with sub-tabs",
    ],
  },
];

const NAVY = "#1A2280";
const GOLD = "#D4921A";

// ── Cache clear instructions per browser ──────────────────────────────────
const BROWSER_STEPS = [
  {
    name: "Chrome",
    icon: "🌐",
    steps: [
      "Press Cmd+Shift+R (Mac) or Ctrl+Shift+R (Windows) for a hard refresh",
      "Or: Chrome menu → More Tools → Clear browsing data",
      "Select 'Cached images and files' → Clear data",
      "Alternatively: F12 → Right-click the refresh button → 'Empty Cache and Hard Reload'",
    ],
  },
  {
    name: "Safari",
    icon: "🧭",
    steps: [
      "Hold Shift and click the Refresh button",
      "Or: Develop menu → Empty Caches (enable Develop menu in Safari Preferences → Advanced)",
      "Or: Safari menu → Clear History → select 'all history'",
    ],
  },
  {
    name: "Firefox",
    icon: "🦊",
    steps: [
      "Press Ctrl+Shift+Delete to open Clear Data dialog",
      "Check 'Cached Web Content' → Clear Now",
      "Or: Ctrl+F5 for a hard refresh",
    ],
  },
  {
    name: "Mobile (iOS Safari)",
    icon: "📱",
    steps: [
      "Settings → Safari → Clear History and Website Data",
      "Or: Settings → Safari → Advanced → Website Data → Remove All Website Data",
    ],
  },
  {
    name: "Mobile (Chrome)",
    icon: "📱",
    steps: [
      "Chrome menu (⋮) → Settings → Privacy and security → Clear browsing data",
      "Select 'Cached images and files' → Clear data",
    ],
  },
];

class VersionChecker extends Component {
  state = {
    showModal:    false,
    activeTab:    "whats_new",
    dismissed:    false,
  };

  componentDidMount() {
    this.checkVersion();
  }

  checkVersion = () => {
    const stored = parseInt(localStorage.getItem("mhb_portal_version") || "0");

    if (stored === 0) {
      // First visit — store version silently, no popup
      localStorage.setItem("mhb_portal_version", String(APP_VERSION));
      return;
    }

    if (stored < APP_VERSION) {
      // New version — show the modal
      localStorage.setItem("mhb_portal_version", String(APP_VERSION));
      // Small delay so the page finishes loading first
      setTimeout(() => this.setState({ showModal: true, activeTab: "whats_new" }), 1500);
    }
  };

  // Called externally (e.g. from subscription upgrade flow) to show upgrade notice
  static showUpgradeNotice = (planName) => {
    // Store a flag that LoginPanel/elsewhere can pick up
    localStorage.setItem("mhb_show_upgrade_notice", planName || "true");
  };

  checkUpgradeNotice = () => {
    const plan = localStorage.getItem("mhb_show_upgrade_notice");
    if (plan) {
      localStorage.removeItem("mhb_show_upgrade_notice");
      notification.success({
        message:     "🎉 Plan Upgraded!",
        description: `Welcome to ${plan}! Your new features are ready. Refresh if anything looks different.`,
        duration:    8,
        placement:   "topRight",
      });
    }
  };

  componentDidUpdate(prevProps) {
    // Check for upgrade notice after auth state changes
    if (this.props.context?.state?.userDetails && !prevProps.context?.state?.userDetails) {
      this.checkUpgradeNotice();
    }
  }

  handleClose = () => this.setState({ showModal: false });

  handleClearAndReload = () => {
    // Clear caches then reload
    if ("caches" in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    localStorage.setItem("mhb_portal_version", String(APP_VERSION));
    window.location.reload(true);
  };

  renderWhatsNew = () => {
    return (
      <div style={{ maxHeight: "420px", overflowY: "auto", paddingRight: "4px" }}>
        {WHATS_NEW.map((release, ri) => (
          <div key={ri} style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <div style={{ background: ri === 0 ? NAVY : "#f0f4ff", borderRadius: "8px", padding: "6px 12px" }}>
                <span style={{ fontSize: "11px", fontWeight: 800, color: ri === 0 ? GOLD : "#718096", textTransform: "uppercase", letterSpacing: ".05em" }}>
                  {ri === 0 ? "✨ Latest" : `v${release.version}`}
                </span>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "14px", color: NAVY }}>{release.title}</div>
                <div style={{ fontSize: "11px", color: "#718096" }}>{release.date}</div>
              </div>
            </div>
            <div style={{ paddingLeft: "8px" }}>
              {release.features.map((f, fi) => (
                <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "7px" }}>
                  <CheckCircleOutlined style={{ color: "#38A169", fontSize: "13px", marginTop: "2px", flexShrink: 0 }} />
                  <span style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.5 }}>{f}</span>
                </div>
              ))}
            </div>
            {ri < WHATS_NEW.length - 1 && <div style={{ borderTop: "1px dashed #E2E8F0", marginTop: "16px" }} />}
          </div>
        ))}
      </div>
    );
  };

  renderCacheHelp = () => (
    <div style={{ maxHeight: "420px", overflowY: "auto", paddingRight: "4px" }}>
      <div style={{ background: "#fff8f0", border: "1px solid #f6d5a8", borderRadius: "10px", padding: "12px 16px", marginBottom: "16px" }}>
        <div style={{ fontWeight: 700, color: "#744210", fontSize: "13px", marginBottom: "4px" }}>
          ⚠️ Why do I need to clear my cache?
        </div>
        <div style={{ fontSize: "12px", color: "#7B341E", lineHeight: 1.6 }}>
          Browsers save a copy of the site to load it faster. When we push updates, your browser may still show the old version. Clearing the cache forces it to download the latest version — this fixes most display issues and missing features after an update.
        </div>
      </div>

      <Button
        type="primary" icon={<ClearOutlined />} block
        onClick={this.handleClearAndReload}
        style={{ background: NAVY, borderColor: NAVY, borderRadius: "50px", fontWeight: 700, marginBottom: "16px", height: "40px" }}>
        🔄 Clear Cache &amp; Reload Now (Recommended)
      </Button>

      <div style={{ fontSize: "12px", color: "#718096", marginBottom: "14px", textAlign: "center" }}>
        — or clear manually for your browser —
      </div>

      {BROWSER_STEPS.map((browser, bi) => (
        <div key={bi} style={{ marginBottom: "14px", border: "1px solid #E2E8F0", borderRadius: "10px", overflow: "hidden" }}>
          <div style={{ background: "#F7F9FC", padding: "8px 14px", fontWeight: 700, fontSize: "13px", color: NAVY, borderBottom: "1px solid #E2E8F0" }}>
            {browser.icon} {browser.name}
          </div>
          <div style={{ padding: "10px 14px" }}>
            {browser.steps.map((step, si) => (
              <div key={si} style={{ display: "flex", gap: "8px", marginBottom: "6px", alignItems: "flex-start" }}>
                <span style={{ background: NAVY, color: "white", borderRadius: "50%", width: "18px", height: "18px", fontSize: "10px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
                  {si + 1}
                </span>
                <span style={{ fontSize: "12px", color: "#4A5568", lineHeight: 1.5 }}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  render() {
    const { showModal, activeTab } = this.state;
    const latestRelease = WHATS_NEW[0];

    return (
      <Modal
        visible={showModal}
        onCancel={this.handleClose}
        footer={null}
        width={560}
        style={{ top: 40 }}
        bodyStyle={{ padding: 0 }}
        className="modal-black-close">

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`, padding: "24px 28px", borderRadius: "8px 8px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
            <RocketOutlined style={{ color: GOLD, fontSize: "24px" }} />
            <div>
              <div style={{ color: "white", fontWeight: 800, fontSize: "18px" }}>
                My Home Buddha Updated
              </div>
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px" }}>
                Version {APP_VERSION} · {latestRelease.date}
              </div>
            </div>
          </div>
          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", lineHeight: 1.6, marginTop: "8px" }}>
            {latestRelease.title}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: "0 4px" }}>
          <Tabs activeKey={activeTab} onChange={key => this.setState({ activeTab: key })}
            tabBarStyle={{ padding: "0 24px", marginBottom: 0 }}>
            <TabPane tab={<span><BellOutlined /> What's New</span>} key="whats_new">
              <div style={{ padding: "20px 24px 0" }}>
                {this.renderWhatsNew()}
              </div>
            </TabPane>
            <TabPane tab={<span><ClearOutlined /> Clear Cache</span>} key="cache">
              <div style={{ padding: "20px 24px 0" }}>
                {this.renderCacheHelp()}
              </div>
            </TabPane>
          </Tabs>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px 20px", display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #E2E8F0", marginTop: "16px" }}>
          <Button onClick={() => this.setState({ activeTab: "cache" })}
            style={{ borderRadius: "50px", borderColor: "#E2E8F0", color: "#718096" }}>
            <ClearOutlined /> Having issues? Clear cache
          </Button>
          <Button type="primary" onClick={this.handleClose}
            style={{ background: NAVY, borderColor: NAVY, borderRadius: "50px", fontWeight: 700 }}>
            Got it — Let's go! 🚀
          </Button>
        </div>
      </Modal>
    );
  }
}

export default VersionChecker;
